import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import { getClaimPreviewAPI } from '@apiRoutes'
import type { ClaimPreviewStatus } from '@apiRoutes'
import { Loading, PrimaryButton, SecondaryButton } from '@components'
import {
  ClaimErrorDetails,
  ClaimMessageKey,
  getClaimErrorCode
} from './claim-errors'
import { ClaimDoneState, performClaim } from './perform-claim'
import {
  ClaimLayout,
  ClaimMessage,
  buildLoginOptions,
  claimDonePath,
  fillText,
  model
} from './shared'
import styles from './index.module.scss'

// Same rule the API enforces; anything else is a 404 there, so skip the request.
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,128}$/

// "Hide My Email" with Sign in with Apple: patients won't recognise the address
const isApplePrivateRelay = (email: string) =>
  /@privaterelay\.appleid\.com$/i.test(email)

type Preview = {
  status: ClaimPreviewStatus
  practiceName: string
  clinicianName?: string | null
}

const Claim: FC = () => {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
    loginWithRedirect,
    getAccessTokenSilently
  } = useAuth0()

  const [preview, setPreview] = useState<Preview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(true)
  const [previewAttempt, setPreviewAttempt] = useState(0)
  const [messageKey, setMessageKey] = useState<ClaimMessageKey | null>(null)
  const [messageDetails, setMessageDetails] = useState<ClaimErrorDetails>()
  const [claiming, setClaiming] = useState(false)
  const claimAttempted = useRef(false)

  // 1. Public preview: enough to show expired/used links before sign-in.
  useEffect(() => {
    let cancelled = false

    if (!TOKEN_PATTERN.test(token)) {
      setPreview(null)
      setMessageKey('not_found')
      setPreviewLoading(false)
      return
    }

    setPreviewLoading(true)
    setMessageKey(null)

    getClaimPreviewAPI(token)
      .then((response) => {
        if (cancelled) return

        if (response.success) {
          const payload = response.responsePayload
          setPreview(payload)
          setMessageKey(payload.status === 'ready' ? null : payload.status)
        } else {
          setPreview(null)
          setMessageKey(
            response.status === 404
              ? 'not_found'
              : getClaimErrorCode(
                  response.status,
                  response.responsePayload?.code
                )
          )
        }
      })
      .catch(() => {
        if (cancelled) return
        setPreview(null)
        setMessageKey('network')
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [token, previewAttempt])

  const claim = useCallback(async () => {
    claimAttempted.current = true
    setClaiming(true)
    setMessageKey(null)

    const outcome = await performClaim(getAccessTokenSilently, token)

    if (outcome.ok) {
      const state: ClaimDoneState = {
        claim: outcome.claim,
        practiceName: preview?.practiceName
      }
      navigate(claimDonePath(token), { replace: true, state })
      return
    }

    setMessageKey(outcome.code)
    setMessageDetails(outcome.details)
    setClaiming(false)
  }, [getAccessTokenSilently, navigate, preview, token])

  // 2. Signed in and the link was already used: re-check it. If this account
  //    claimed it, the idempotent POST returns 200 and we show the done page;
  //    otherwise 409. Nothing new is activated, so no confirmation is needed.
  //    An unused ("ready") link waits for the patient to confirm the account:
  //    an existing Auth0 session may belong to someone else.
  useEffect(() => {
    if (previewLoading || authLoading || !isAuthenticated || !preview) return
    if (claimAttempted.current) return
    if (preview.status !== 'claimed') return

    claim()
  }, [authLoading, claim, isAuthenticated, preview, previewLoading])

  const signIn = (
    screenHint: 'login' | 'signup',
    forceAccountChoice = false
  ) => {
    loginWithRedirect(
      buildLoginOptions(location.pathname, screenHint, forceAccountChoice)
    )
  }

  const handleAction = (action: 'retry' | 'signInOther' | 'signIn') => {
    if (action === 'signInOther') {
      return signIn('login', true)
    }
    if (action === 'signIn') {
      return signIn('login')
    }
    // retry: if the preview never loaded, reload it; otherwise retry the claim.
    if (!preview) {
      claimAttempted.current = false
      return setPreviewAttempt((n) => n + 1)
    }
    return claim()
  }

  const practiceName = preview?.practiceName

  const usable =
    !!preview && (preview.status === 'ready' || preview.status === 'claimed')
  // Waiting on Auth0, or signed in and about to re-check a used link: don't flash the sign-in screen.
  const autoClaimPending =
    usable &&
    !claimAttempted.current &&
    (authLoading || (isAuthenticated && preview?.status === 'claimed'))

  const renderBody = () => {
    if (previewLoading) {
      return (
        <div className={styles.loading} data-testid="claim-loading">
          <Loading />
        </div>
      )
    }

    if (claiming || autoClaimPending) {
      const activating = claiming || isAuthenticated
      return (
        <div
          className={styles.loading}
          data-testid={activating ? 'claim-activating' : 'claim-loading'}
        >
          <Loading />
          {activating && <p className={styles.body}>{model.activating}</p>}
        </div>
      )
    }

    if (messageKey) {
      return (
        <ClaimMessage
          messageKey={messageKey}
          practiceName={practiceName}
          details={messageDetails}
          onAction={handleAction}
        />
      )
    }

    if (!preview || preview.status !== 'ready') {
      return <ClaimMessage messageKey="unknown" onAction={handleAction} />
    }

    if (isAuthenticated) {
      const email = user?.email || null
      return (
        <div className={styles.ready} data-testid="claim-confirm">
          <header>
            <h2 className={styles.title}>{model.confirm.title}</h2>
            <p className={styles.body}>
              {fillText(model.confirm.intro, {
                practice: preview.practiceName
              })}
            </p>
          </header>
          <dl className={styles.kv}>
            <dt>{model.confirm.account}</dt>
            <dd data-testid="claim-confirm-account">
              {email || model.confirm.accountFallback}
            </dd>
            <dt>{model.ready.providedBy}</dt>
            <dd>{preview.practiceName}</dd>
            <dt>{model.ready.length}</dt>
            <dd>{model.ready.lengthValue}</dd>
          </dl>
          {email && isApplePrivateRelay(email) && (
            <p className={styles.help}>{model.confirm.appleRelay}</p>
          )}
          <div className={styles.actions}>
            <PrimaryButton
              context="claim-confirm"
              className={styles.button}
              caption={model.confirm.activate}
              onClick={() => claim()}
            />
            <SecondaryButton
              context="claim-use-other-account"
              className={styles.secondary_button}
              caption={model.confirm.useOther}
              onClick={() => signIn('login', true)}
            />
          </div>
          <p className={styles.help}>{model.confirm.help}</p>
        </div>
      )
    }

    return (
      <div className={styles.ready} data-testid="claim-ready">
        <header>
          <h2 className={styles.title}>{model.ready.title}</h2>
          <p className={styles.body}>
            {fillText(model.ready.intro, { practice: preview.practiceName })}
          </p>
        </header>
        <dl className={styles.kv}>
          <dt>{model.ready.providedBy}</dt>
          <dd>{preview.practiceName}</dd>
          {preview.clinicianName && (
            <>
              <dt>{model.ready.orderedBy}</dt>
              <dd>{preview.clinicianName}</dd>
            </>
          )}
          <dt>{model.ready.length}</dt>
          <dd>{model.ready.lengthValue}</dd>
          <dt>{model.ready.cost}</dt>
          <dd>{model.ready.costValue}</dd>
        </dl>
        <div className={styles.actions}>
          <PrimaryButton
            context="claim-sign-in"
            className={styles.button}
            caption={model.ready.signIn}
            onClick={() => signIn('login')}
          />
          <SecondaryButton
            context="claim-create-account"
            className={styles.secondary_button}
            caption={model.ready.createAccount}
            onClick={() => signIn('signup')}
          />
        </div>
        <p className={styles.help}>{model.ready.help}</p>
      </div>
    )
  }

  return <ClaimLayout practiceName={practiceName}>{renderBody()}</ClaimLayout>
}

export default Claim
